<template>
    <div>
        <div>
            <div class="text-center">
                <h1 class="text-3xl">
                    Venta rápida
                    <span
                        v-if="selectedMerchant.merchantId"
                        class="text-theme-primary capitalize"
                    >
                        {{
                            selectedMerchant.merchantType | merchantInfo("type")
                        }}
                        {{ selectedMerchant.name }}
                    </span>
                </h1>
            </div>
            <div class="text-justify mt-3">
                <p>
                    Puedes generar una transacción de venta sin la necesidad de
                    asociar inventario. Además, el dinero en la caja se
                    incrementará automáticamente.
                </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
                <div>
                    <custom-checkbox v-model="saleNeedComments" warn>
                        <span> Agregar comentarios </span>
                    </custom-checkbox>
                </div>
                <div
                    v-if="
                        $ability.hasPermission(
                            'order:CreateWithCustomProductsNames'
                        )
                    "
                >
                    <custom-checkbox v-model="saleNeedBarcode" warn>
                        <span> Agregar códigos de barras </span>
                    </custom-checkbox>
                </div>
                <div v-if="$ability.hasPermission('customer:List')">
                    <custom-checkbox v-model="orderNeedCustomer" success>
                        <span>
                            {{
                                customerName && orderNeedCustomer
                                    ? customerName
                                    : "Asociar venta a cliente"
                            }}
                        </span>
                    </custom-checkbox>
                </div>
            </div>

            <div class="mt-3">
                <payment-methods-checker v-model="paymentMethod">
                    <template #title>
                        <span class="mb-3">Método de pago *</span>
                    </template>
                </payment-methods-checker>
            </div>

            <div class="mt-3">
                <span>Valor de venta *</span>
                <input-money-component-2
                    v-model="priceOffer"
                    border
                    :class="backgroundColorTextArea"
                    enabled
                    label=""
                    icon="💲"
                />
            </div>

            <div v-if="saleNeedComments" class="mt-3">
                <span>Comentarios</span>
                <div>
                    <textarea
                        v-model="observation"
                        class="app-textarea mt-2 text-lg"
                        :class="backgroundColorTextArea"
                        placeholder="Información relacionada"
                        rows="2"
                    ></textarea>
                </div>
            </div>

            <div v-if="saleNeedBarcode" class="mt-3">
                <span>
                    Códigos de barras de productos
                    <small> (Separados por ',') </small>
                </span>
                <div>
                    <array-string-selector
                        v-model="productsName"
                        class="mt-2"
                        label=""
                        enabled
                    />
                </div>
            </div>

            <div class="mt-8 flex justify-center">
                <app-button
                    id="create-order"
                    class="mb-5 w-full md:w-3/4 mx-auto"
                    border
                    gradient
                    @click="createOrderComponent"
                >
                    <span class="text-2xl"> Crear venta </span>
                </app-button>
            </div>
        </div>
        <app-dialog v-model="isOpenModalResult" @close="closeResult">
            <template #header>
                <span class="text-2xl"> El dinero ingresado debe ser </span>
            </template>
            <div>
                <div class="flex flex-col justify-center items-center">
                    <span class="text-theme-primary text-4xl">
                        {{ moneyIngress | currency }}
                    </span>
                    <div></div>
                    <div v-if="moneyBack" class="flex flex-col text-xl">
                        <span>
                            Vuelto de:
                            <strong>
                                {{ moneyBack | currency }}
                            </strong>
                        </span>
                        <span>
                            Venta de:
                            <strong>
                                {{ orderResult.total | currency }}
                            </strong>
                        </span>
                    </div>
                </div>
                <div class="mt-4 flex justify-center">
                    <app-button
                        @click="
                            showOrderTicketPrinter = !showOrderTicketPrinter
                        "
                    >
                        🖨 Vale de compra
                    </app-button>
                    <app-button @click="isOpenModalResult = false">
                        💰 Registrar venta
                    </app-button>
                    <app-button
                        :to="
                            $enabledEcommerceOptions
                                ? `/app/orders/${selectedMerchant.merchantType}/${selectedMerchant.merchantId}/detail/${orderResult.id}`
                                : `/app/orders/detail/${orderResult.id}`
                        "
                    >
                        🐱‍💻 Detalles
                    </app-button>
                </div>

                <div v-if="showOrderTicketPrinter" class="mt-5">
                    <order-ticket-printer
                        show
                        show-print
                        :order="orderResult"
                    />
                </div>
            </div>
        </app-dialog>

        <app-dialog
            v-model="isOpenModalCustomers"
            size="lg"
            @close="orderNeedCustomer = !!customerId"
        >
            <template #header>
                <h3>Buscador de clientes 👩‍🎤</h3>
            </template>

            <search-user is-customer @change="selectCustomer" />
        </app-dialog>

        <app-dialog
            v-model="isOpenModalMoneyBack"
            persistent
            @close="onEnterMoneyIngress"
        >
            <template #header>
                <span class="text-2xl">
                    Valor de venta {{ orderResult.total | currency }}
                </span>
            </template>
            <div class="input-number-simple flex flex-col justify-center">
                <div class="pt-2">
                    <span class="text-lg"> Ingresa el monto con que paga </span>
                </div>
                <div class="flex items-center">
                    <div>
                        <input-money-component-2
                            v-model="moneyIngress"
                            autofocus
                            @enter="isOpenModalMoneyBack = false"
                        />
                    </div>
                    <app-button
                        :disabled="!isValidMoneyIngress"
                        icon
                        circle
                        @click="isOpenModalMoneyBack = false"
                    >
                        ✔️
                    </app-button>
                </div>

                <div v-if="!isValidMoneyIngress" class="mt-3">
                    <span class="text-theme-warning">
                        El dinero ingresado debe ser mayor a el valor de la
                        venta.
                    </span>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import OrderTicketPrinter from "@/components/orders/OrderTicketPrinter.vue";
import PaymentMethodsChecker from "@/components/orders/PaymentMethodsChecker.vue";
import SearchUser from "@/components/users/SearchUser.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import ArrayStringSelector from "@/components/utils/ArrayStringSelector.vue";
import InputMoneyComponent2 from "@/components/utils/InputMoneyComponent2.vue";

export default {
    name: "RecordOrder",
    components: {
        AppDialog,
        SearchUser,
        OrderTicketPrinter,
        PaymentMethodsChecker,
        ArrayStringSelector,
        InputMoneyComponent2
    },
    data: () => ({
        observation: "",
        priceOffer: 0,
        moneyIngress: 0,
        moneyBack: 0,
        customerName: "",
        customerId: null,
        isOpenModalMoneyBack: false,
        saleNeedBarcode: false,
        saleNeedComments: false,
        isOpenModalResult: false,
        orderNeedCustomer: false,
        isOpenModalCustomers: false,
        showOrderTicketPrinter: false,
        paymentMethod: "cash",
        productsName: [],
        orderResult: {
            id: 0,
            uuid: "",
            total: 20,
            createdAt: "2022-07-15T02:39:03.833Z",
            references: [],
            anonymousReferences: [],
            customer: {
                name: ""
            }
        }
    }),
    computed: {
        ...mapGetters("control", [
            "backgroundColor",
            "backgroundColorTextArea"
        ]),
        ...mapState("control", ["activeDark"]),
        ...mapState("commons", ["selectedMerchant"]),
        isValidMoneyIngress() {
            return this.moneyIngress >= this.orderResult.total;
        }
    },
    watch: {
        orderNeedCustomer(value) {
            if (value) {
                this.enabledScanner = false;
                this.isOpenModalCustomers = true;
            }
        }
    },
    mounted() {},
    methods: {
        ...mapActions("orders", ["createOrder"]),
        async createOrderComponent() {
            this.$loading.show();
            try {
                const hasMerchant = !!this.selectedMerchant.merchantId;
                const order = await this.createOrder({
                    ...(this.orderNeedCustomer
                        ? { customerId: this.customerId }
                        : null),
                    ...(hasMerchant
                        ? {
                              merchantId: this.selectedMerchant.merchantId,
                              merchantType: this.selectedMerchant.merchantType,
                              sourcePriceMerchant: true
                          }
                        : null),
                    paymentMethod: this.paymentMethod,
                    observation: this.observation,
                    anonymousReferences: [
                        {
                            priceOffer: this.priceOffer,
                            appliedDiscountPerReference: 0,
                            quantity: 1,
                            product: {
                                name:
                                    this.productsName.join(",") ||
                                    "No determinado"
                            }
                        }
                    ],
                    verifyAndDecreaseInventory: false,
                    references: []
                });

                this.orderResult = {
                    id: order.id,
                    uuid: order.uuid,
                    customerName: "Anónimo",
                    customer: {
                        firstName: "Anónimo"
                    },
                    createdAt: order.createdAt,
                    paymentStatus: order.paymentStatus,
                    paymentMethod: order.paymentMethod,
                    total: order.total,
                    references: [],
                    anonymousReferences: []
                };
                this.$loading.hide();
                this.moneyIngress = order.total;
                this.$sound.play("cash", 0.4);
                this.observation = "";
                this.productsName = [];
                this.priceOffer = 0;
                if (this.paymentMethod === "cash") {
                    this.isOpenModalMoneyBack = true;
                    return;
                }
                this.isOpenModalResult = true;
                setTimeout(() => {
                    this.isOpenModalResult = false;
                }, 60000);
            } catch (error) {
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onEnterMoneyIngress() {
            this.isOpenModalResult = true;
            this.moneyBack = this.moneyIngress - this.orderResult.total;
        },
        closeResult() {
            this.paymentMethod = "cash";
            this.showOrderTicketPrinter = false;
        },
        selectCustomer(customer) {
            this.customerId = customer.id;
            this.customerName = `${customer.firstName} ${customer.lastName}`;
            this.isOpenModalCustomers = false;
        }
    }
};
</script>
